import { AppDispatch } from '../store';
import { setOnlineStatus } from '../store/slices/trainingSlice';
import logger from '../utils/logger';

/**
 * Service to monitor and manage network status
 */
class NetworkService {
  private static instance: NetworkService;
  private listeners: Array<(isOnline: boolean) => void> = [];
  private lastKnownStatus: boolean = navigator.onLine;
  private dispatch: AppDispatch | null = null;

  private constructor() {
    this.setupEventListeners();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): NetworkService {
    if (!NetworkService.instance) {
      NetworkService.instance = new NetworkService();
    }
    return NetworkService.instance;
  }

  /**
   * Initialize with Redux dispatch
   */
  public initialize(dispatch: AppDispatch): void {
    this.dispatch = dispatch;
    
    // Immediately dispatch current status
    if (this.dispatch) {
      this.dispatch(setOnlineStatus(this.lastKnownStatus));
    }
  }

  /**
   * Add a listener for network status changes
   */
  public addListener(listener: (isOnline: boolean) => void): () => void {
    this.listeners.push(listener);
    
    // Immediately notify the listener of current status
    listener(this.lastKnownStatus);
    
    // Return a function to remove this listener
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Get current online status
   */
  public isOnline(): boolean {
    return this.lastKnownStatus;
  }

  /**
   * Test connection to a specific endpoint
   */
  public async testConnection(url: string = 'https://firebase.googleapis.com/ping'): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      await fetch(url, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      return true;
    } catch (error) {
      logger.error('Connection test failed:', error);
      return false;
    }
  }

  /**
   * Setup browser event listeners
   */
  private setupEventListeners(): void {
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  /**
   * Handle online event
   */
  private handleOnline = (): void => {
    logger.info('NetworkService: Device is online');
    this.lastKnownStatus = true;
    
    // Update Redux state
    if (this.dispatch) {
      this.dispatch(setOnlineStatus(true));
    }
    
    // Notify listeners
    this.notifyListeners();
  };

  /**
   * Handle offline event
   */
  private handleOffline = (): void => {
    logger.info('NetworkService: Device is offline');
    this.lastKnownStatus = false;
    
    // Update Redux state
    if (this.dispatch) {
      this.dispatch(setOnlineStatus(false));
    }
    
    // Notify listeners
    this.notifyListeners();
  };

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.lastKnownStatus);
      } catch (error) {
        logger.error('Error in network status listener:', error);
      }
    });
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    this.listeners = [];
  }
}

export default NetworkService.getInstance();